/**
 * Supabase Mutations
 *
 * Mutation hooks for all entities with automatic cache invalidation.
 * These hooks integrate with the mutation manager for optimistic updates.
 */

import { useState, useCallback } from 'react';
import { supabase } from '~/supabase-client';
import { mutationManager } from './mutation-manager';

/**
 * Generic mutation hook factory
 * @param {string} entity - Entity name
 * @returns {Object} - Mutation hook methods
 */
const useMutation = (entity) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (mutationFn, options = {}) => {
    setLoading(true);
    setError(null);

    const result = await mutationManager.mutate({
      entity,
      ...options,
      mutationFn,
      onError: (err) => {
        setError(err);
        if (options.onError) options.onError(err);
      },
      onSuccess: (data) => {
        setError(null);
        if (options.onSuccess) options.onSuccess(data);
      }
    });

    setLoading(false);
    return result;
  }, [entity]);

  return { execute, loading, error };
}

/**
 * Crop Mutations
 */
export const useCreateCrop = () => {
  const { execute, loading, error } = useMutation('crops');

  const createCrop = useCallback(async (cropData, options = {}) => {
    return execute(
      async () => {
        const { template_id, archetype_code, family_code, growth_code, lifecycle_code, notes, ...coreData } = cropData;

        const { data, error } = await supabase
          .from('crops')
          .insert(coreData)
          .select('*')
          .single();

        if (error) return { data, error };

        // Only non-succession crops get a crop_details record
        if (!coreData.parent_crop_id) {
          const { error: detailsError } = await supabase
            .from('crop_details')
            .insert({ crop_id: data.id, organization_id: coreData.organization_id, template_id, archetype_code, family_code, growth_code, lifecycle_code, notes });
          if (detailsError) return { data: null, error: detailsError };
        }

        return { data, error: null };
      },
      {
        operation: 'create',
        optimisticData: cropData,
        ...options
      }
    );
  }, [execute]);

  return { createCrop, loading, error };
}

export const useCreateCropsBulk = () => {
  const { execute, loading, error } = useMutation('crops');

  const createCropsBulk = useCallback(async (cropsData, options = {}) => {
    return execute(
      async () => {
        if (!cropsData || cropsData.length === 0) {
          return { data: [], error: null };
        }

        const { data, error } = await supabase
          .from('crops')
          .insert(cropsData)
          .select()

        return { data, error };
      },
      {
        operation: 'create',
        optimisticData: [],
        ...options
      }
    );
  }, [execute]);

  return { createCropsBulk, loading, error };
}

export const useUpdateCrop = () => {
  const { execute, loading, error } = useMutation('crops');

  const updateCrop = useCallback(async (id, updates, options = {}) => {
    return execute(
      async () => {
        const { template_id, archetype_code, family_code, growth_code, lifecycle_code, notes, ...coreUpdates } = updates;

        const { data, error } = await supabase
          .from('crops')
          .update(coreUpdates)
          .eq('id', id)
          .select('*')
          .single();

        if (error) return { data, error };

        // Only parent crops (parent_crop_id IS NULL) have a crop_details row
        if (!data.parent_crop_id) {
          const { error: detailsError } = await supabase
            .from('crop_details')
            .upsert({ crop_id: id, organization_id: data.organization_id, template_id, archetype_code, family_code, growth_code, lifecycle_code, notes });
          if (detailsError) return { data: null, error: detailsError };
        }

        return { data, error: null };
      },
      {
        operation: 'update',
        optimisticData: { id, ...updates },
        ...options
      }
    );
  }, [execute]);

  return { updateCrop, loading, error };
}

export const useDeleteCrop = () => {
  const { execute, loading, error } = useMutation('crops');

  const deleteCrop = useCallback(async (id, options = {}) => {
    return execute(
      async () => {
        const { error } = await supabase
          .from('crops')
          .delete()
          .eq('id', id);

        return { data: { id }, error };
      },
      {
        operation: 'delete',
        optimisticData: { id },
        ...options
      }
    );
  }, [execute]);

  return { deleteCrop, loading, error };
}

/**
 * Organization Mutations
 */
export const useCreateOrganization = () => {
  const { execute, loading, error } = useMutation('organizations');

  const createOrganization = useCallback(async (orgData, options = {}) => {
    return execute(
      async () => {
        const { data, error } = await supabase
          .from('organizations')
          .insert(orgData)
          .select()
          .single();

        return { data, error };
      },
      {
        operation: 'create',
        optimisticData: orgData,
        ...options
      }
    );
  }, [execute]);

  return { createOrganization, loading, error };
}

export const useUpdateOrganization = () => {
  const { execute, loading, error } = useMutation('organizations');

  const updateOrganization = useCallback(async (id, updates, options = {}) => {
    return execute(
      async () => {
        const { data, error } = await supabase
          .from('organizations')
          .update(updates)
          .eq('id', id)
          .select()
          .single();

        return { data, error };
      },
      {
        operation: 'update',
        optimisticData: { id, ...updates },
        ...options
      }
    );
  }, [execute]);

  return { updateOrganization, loading, error };
}

export const useDeleteOrganization = () => {
  const { execute, loading, error } = useMutation('organizations');

  const deleteOrganization = useCallback(async (id, options = {}) => {
    return execute(
      async () => {
        const { error } = await supabase
          .from('organizations')
          .delete()
          .eq('id', id);

        return { data: { id }, error };
      },
      {
        operation: 'delete',
        optimisticData: { id },
        ...options
      }
    );
  }, [execute]);

  return { deleteOrganization, loading, error };
}

/**
 * Polygon Mutations
 */
export const useCreatePolygon = () => {
  const { execute, loading, error } = useMutation('organization_polygons');

  const createPolygon = useCallback(async (polygonData, options = {}) => {
    return execute(
      async () => {
        const { data, error } = await supabase
          .from('organization_polygons')
          .insert(polygonData)
          .select()
          .single();

        return { data, error };
      },
      {
        operation: 'create',
        optimisticData: polygonData,
        ...options
      }
    );
  }, [execute]);

  return { createPolygon, loading, error };
}

export const useUpdatePolygon = () => {
  const { execute, loading, error } = useMutation('organization_polygons');

  const updatePolygon = useCallback(async (id, updates, options = {}) => {
    return execute(
      async () => {
        const { data, error } = await supabase
          .from('organization_polygons')
          .update(updates)
          .eq('id', id)
          .select()
          .single();

        return { data, error };
      },
      {
        operation: 'update',
        optimisticData: { id, ...updates },
        ...options
      }
    );
  }, [execute]);

  return { updatePolygon, loading, error };
}

export const useDeletePolygon = () => {
  const { execute, loading, error } = useMutation('organization_polygons');

  const deletePolygon = useCallback(async (id, options = {}) => {
    return execute(
      async () => {
        const { error } = await supabase
          .from('organization_polygons')
          .delete()
          .eq('id', id);

        return { data: { id }, error };
      },
      {
        operation: 'delete',
        optimisticData: { id },
        ...options
      }
    );
  }, [execute]);

  return { deletePolygon, loading, error };
}

/**
 * Reference Polygon Mutations
 */
export const useCreateReferencePolygon = () => {
  const { execute, loading, error } = useMutation('reference_polygons');

  const createReferencePolygon = useCallback(async (polygonData, options = {}) => {
    return execute(
      async () => {
        const { data, error } = await supabase
          .from('reference_polygons')
          .insert(polygonData)
          .select()
          .single();

        return { data, error };
      },
      {
        operation: 'create',
        optimisticData: polygonData,
        ...options
      }
    );
  }, [execute]);

  return { createReferencePolygon, loading, error };
}

export const useUpdateReferencePolygon = () => {
  const { execute, loading, error } = useMutation('reference_polygons');

  const updateReferencePolygon = useCallback(async (id, updates, options = {}) => {
    return execute(
      async () => {
        const { data, error } = await supabase
          .from('reference_polygons')
          .update(updates)
          .eq('id', id)
          .select()
          .single();

        return { data, error };
      },
      {
        operation: 'update',
        optimisticData: { id, ...updates },
        ...options
      }
    );
  }, [execute]);

  return { updateReferencePolygon, loading, error };
}

export const useDeleteReferencePolygon = () => {
  const { execute, loading, error } = useMutation('reference_polygons');

  const deleteReferencePolygon = useCallback(async (id, options = {}) => {
    return execute(
      async () => {
        const { error } = await supabase
          .from('reference_polygons')
          .delete()
          .eq('id', id);

        return { data: { id }, error };
      },
      {
        operation: 'delete',
        optimisticData: { id },
        ...options
      }
    );
  }, [execute]);

  return { deleteReferencePolygon, loading, error };
}

/**
 * Crop Template Mutations
 */
export const useCreateCropTemplate = () => {
  const { execute, loading, error } = useMutation('crop_templates');

  const createTemplate = useCallback(async (templateData, options = {}) => {
    return execute(
      async () => {
        const { data, error } = await supabase
          .from('crop_templates')
          .insert(templateData)
          .select()
          .single();

        return { data, error };
      },
      {
        operation: 'create',
        optimisticData: templateData,
        ...options
      }
    );
  }, [execute]);

  return { createTemplate, loading, error };
}

export const useUpdateCropTemplate = () => {
  const { execute, loading, error } = useMutation('crop_templates');

  const updateTemplate = useCallback(async (id, updates, options = {}) => {
    return execute(
      async () => {
        const { data, error } = await supabase
          .from('crop_templates')
          .update(updates)
          .eq('id', id)
          .select()
          .single();

        return { data, error };
      },
      {
        operation: 'update',
        optimisticData: { id, ...updates },
        ...options
      }
    );
  }, [execute]);

  return { updateTemplate, loading, error };
}

export const useDeleteCropTemplate = () => {
  const { execute, loading, error } = useMutation('crop_templates');

  const deleteTemplate = useCallback(async (id, options = {}) => {
    return execute(
      async () => {
        const { error } = await supabase
          .from('crop_templates')
          .delete()
          .eq('id', id);

        return { data: { id }, error };
      },
      {
        operation: 'delete',
        optimisticData: { id },
        ...options
      }
    );
  }, [execute]);

  return { deleteTemplate, loading, error };
}

/**
 * Crop Schedule Mutations
 */
export const useCreateCropSchedule = () => {
  const { execute, loading, error } = useMutation('crop_schedules');

  const createSchedule = useCallback(async (scheduleData, options = {}) => {
    return execute(
      async () => {
        const { data, error } = await supabase
          .from('crop_schedules')
          .insert(scheduleData)
          .select()
          .single();

        return { data, error };
      },
      {
        operation: 'create',
        optimisticData: scheduleData,
        ...options
      }
    );
  }, [execute]);

  return { createSchedule, loading, error };
}

export const useCreateCropSchedulesBulk = () => {
  const { execute, loading, error } = useMutation('crop_schedules');

  const createSchedulesBulk = useCallback(async (schedulesData, options = {}) => {
    return execute(
      async () => {
        if (!schedulesData || schedulesData.length === 0) {
          return { data: [], error: null };
        }

        const { data, error } = await supabase
          .from('crop_schedules')
          .insert(schedulesData)
          .select()

        return { data, error };
      },
      { operation: 'create', optimisticData: [], ...options }
    );
  }, [execute]);

  return { createSchedulesBulk, loading, error };
}

export const useUpdateCropSchedule = () => {
  const { execute, loading, error } = useMutation('crop_schedules');

  const updateSchedule = useCallback(async (id, updates, options = {}) => {
    return execute(
      async () => {
        const { data, error } = await supabase
          .from('crop_schedules')
          .update(updates)
          .eq('id', id)
          .select()
          .single();

        return { data, error };
      },
      {
        operation: 'update',
        optimisticData: { id, ...updates },
        ...options
      }
    );
  }, [execute]);

  return { updateSchedule, loading, error };
}

export const useDeleteCropSchedule = () => {
  const { execute, loading, error } = useMutation('crop_schedules');

  const deleteSchedule = useCallback(async (id, options = {}) => {
    return execute(
      async () => {
        const { error } = await supabase
          .from('crop_schedules')
          .delete()
          .eq('id', id);

        return { data: { id }, error };
      },
      {
        operation: 'delete',
        optimisticData: { id },
        ...options
      }
    );
  }, [execute]);

  return { deleteSchedule, loading, error };
}

/**
 * Crop Phase Mutations
 */
export const useCreateCropPhase = () => {
  const { execute, loading, error } = useMutation('crop_phases');

  const createPhase = useCallback(async (phaseData, options = {}) => {
    return execute(
      async () => {
        const { data, error } = await supabase
          .from('crop_phases')
          .insert(phaseData)
          .select(`
            *,
            phase_type:crop_phase_types(code, name, description, typical_duration_days)
          `)
          .single();

        return { data, error };
      },
      {
        operation: 'create',
        optimisticData: phaseData,
        ...options
      }
    );
  }, [execute]);

  return { createPhase, loading, error };
}

export const useCreateCropPhasesBulk = () => {
  const { execute, loading, error } = useMutation('crop_phases');

  const createPhasesBulk = useCallback(async (phasesData, options = {}) => {
    return execute(
      async () => {
        if (!phasesData || phasesData.length === 0) {
          return { data: [], error: null };
        }

        const { data, error } = await supabase
          .from('crop_phases')
          .insert(phasesData)
          .select()

        return { data, error };
      },
      { operation: 'create', optimisticData: [], ...options }
    );
  }, [execute]);

  return { createPhasesBulk, loading, error };
}

export const useUpdateCropPhase = () => {
  const { execute, loading, error } = useMutation('crop_phases');

  const updatePhase = useCallback(async (id, updates, options = {}) => {
    return execute(
      async () => {
        const { data, error } = await supabase
          .from('crop_phases')
          .update(updates)
          .eq('id', id)
          .select(`
            *,
            phase_type:crop_phase_types(code, name, description, typical_duration_days)
          `)
          .single();

        return { data, error };
      },
      {
        operation: 'update',
        optimisticData: { id, ...updates },
        ...options
      }
    );
  }, [execute]);

  return { updatePhase, loading, error };
}

export const useDeleteCropPhase = () => {
  const { execute, loading, error } = useMutation('crop_phases');

  const deletePhase = useCallback(async (id, options = {}) => {
    return execute(
      async () => {
        const { error } = await supabase
          .from('crop_phases')
          .delete()
          .eq('id', id);

        return { data: { id }, error };
      },
      {
        operation: 'delete',
        optimisticData: { id },
        ...options
      }
    );
  }, [execute]);

  return { deletePhase, loading, error };
}

/**
 * User Mutations
 */
export const useUpdateUser = () => {
  const { execute, loading, error } = useMutation('users');

  const updateUser = useCallback(async (id, updates, options = {}) => {
    return execute(
      async () => {
        const { data, error } = await supabase
          .from('users')
          .update(updates)
          .eq('id', id)
          .select()
          .single();

        return { data, error };
      },
      {
        operation: 'update',
        optimisticData: { id, ...updates },
        ...options
      }
    );
  }, [execute]);

  return { updateUser, loading, error };
}

/**
 * Climate Mutations
 */
export const useUpdateClimate = () => {
  const { execute, loading, error } = useMutation('organization_climates');

  const updateClimate = useCallback(async (id, updates, options = {}) => {
    return execute(
      async () => {
        const { data, error } = await supabase
          .from('organization_climates')
          .update(updates)
          .eq('id', id)
          .select()
          .single();

        return { data, error };
      },
      {
        operation: 'update',
        optimisticData: { id, ...updates },
        ...options
      }
    );
  }, [execute]);

  return { updateClimate, loading, error };
}

/**
 * Task Mutations
 */
export const useCreateTask = () => {
  const { execute, loading, error } = useMutation('tasks');

  const createTask = useCallback(async (taskData, options = {}) => {
    return execute(
      async () => {
        const { data, error } = await supabase
          .from('tasks')
          .insert(taskData)
          .select('*, task_types(*), task_states(*)')
          .single();

        return { data, error };
      },
      {
        operation: 'create',
        optimisticData: taskData,
        ...options
      }
    );
  }, [execute]);

  return { createTask, loading, error };
}

export const useCreateTasksBulk = () => {
  const { execute, loading, error } = useMutation('tasks');

  const createTasksBulk = useCallback(async (tasksData, options = {}) => {
    return execute(
      async () => {
        if (!tasksData || tasksData.length === 0) {
          return { data: [], error: null };
        }

        // Bulk insert all tasks in single transaction
        const { data, error } = await supabase
          .from('tasks')
          .insert(tasksData)
          .select('*');

        return { data, error };
      },
      {
        operation: 'create',
        optimisticData: [], // Can't predict IDs for bulk insert
        ...options
      }
    );
  }, [execute]);

  return { createTasksBulk, loading, error };
}

export const useCreateTasksFromSchedule = () => {
  const { execute, loading, error } = useMutation('tasks');

  const createTasks = useCallback(async (scheduleData, options = {}) => {
    // Import the generator dynamically to avoid circular dependencies
    const { generateTasksFromSchedule } = await import('../crop/crop-modal/generators/generate-tasks-from-schedule');

    return execute(
      async () => {
        // Generate task objects from schedule
        const rawTasks = generateTasksFromSchedule(scheduleData, {
          userId: options.userId,
          state: options.state || 'ready',
          includePlotTasks: options.includePlotTasks || false,
          skipTaskTypes: options.skipTaskTypes || []
        });
        const tasks = options.seasonId
          ? rawTasks.map(t => ({ ...t, season_id: options.seasonId }))
          : rawTasks;

        if (tasks.length === 0) {
          return { data: [], error: null };
        }

        // Bulk insert all tasks in single transaction
        const { data, error } = await supabase
          .from('tasks')
          .insert(tasks)
          .select('*, task_types(*), task_states(*)');

        return { data, error };
      },
      {
        operation: 'create',
        optimisticData: [], // Can't predict IDs for bulk insert
        ...options
      }
    );
  }, [execute]);

  return { createTasks, loading, error };
}

export const useCreateTasksFromSchedulesBulk = () => {
  const { execute, loading, error } = useMutation('tasks');

  const createTasksFromSchedulesBulk = useCallback(async (schedulesData, options = {}) => {
    const { generateTasksFromSchedule } = await import('../crop/crop-modal/generators/generate-tasks-from-schedule');

    return execute(
      async () => {
        const rawTasks = schedulesData.flatMap(scheduleData =>
          generateTasksFromSchedule(scheduleData, {
            userId: options.userId,
            state: options.state || 'ready',
            includePlotTasks: options.includePlotTasks || false,
            skipTaskTypes: options.skipTaskTypes || []
          })
        );
        const allTasks = options.seasonId
          ? rawTasks.map(t => ({ ...t, season_id: options.seasonId }))
          : rawTasks;

        if (allTasks.length === 0) {
          return { data: [], error: null };
        }

        const { data, error } = await supabase
          .from('tasks')
          .insert(allTasks)
          .select('*, task_types(*), task_states(*)')

        return { data, error };
      },
      { operation: 'create', optimisticData: [], ...options }
    );
  }, [execute]);

  return { createTasksFromSchedulesBulk, loading, error };
}

export const useUpdateTask = () => {
  const { execute, loading, error } = useMutation('tasks');

  const updateTask = useCallback(async (id, updates, options = {}) => {
    return execute(
      async () => {
        const { data, error } = await supabase
          .from('tasks')
          .update(updates)
          .eq('id', id)
          .select('*, task_types(*), task_states(*)')
          .single();

        return { data, error };
      },
      {
        operation: 'update',
        optimisticData: { id, ...updates },
        ...options
      }
    );
  }, [execute]);

  return { updateTask, loading, error };
}

export const useDeleteTask = () => {
  const { execute, loading, error } = useMutation('tasks');

  const deleteTask = useCallback(async (id, options = {}) => {
    return execute(
      async () => {
        const { error } = await supabase
          .from('tasks')
          .delete()
          .eq('id', id);

        return { data: { id }, error };
      },
      {
        operation: 'delete',
        optimisticData: { id },
        ...options
      }
    );
  }, [execute]);

  return { deleteTask, loading, error };
}

/**
 * Report Mutations
 */
export const useCreateReport = () => {
  const { execute, loading, error } = useMutation('reports');

  const createReport = useCallback(async (reportData, options = {}) => {
    return execute(
      async () => {
        const { data, error } = await supabase
          .from('reports')
          .insert(reportData)
          .select('*, report_types(*)')
          .single();

        return { data, error };
      },
      {
        operation: 'create',
        optimisticData: reportData,
        ...options
      }
    );
  }, [execute]);

  return { createReport, loading, error };
}

export const useUpdateReport = () => {
  const { execute, loading, error } = useMutation('reports');

  const updateReport = useCallback(async (id, updates, options = {}) => {
    return execute(
      async () => {
        const { data, error } = await supabase
          .from('reports')
          .update(updates)
          .eq('id', id)
          .select('*, report_types(*)')
          .single();

        return { data, error };
      },
      {
        operation: 'update',
        optimisticData: { id, ...updates },
        ...options
      }
    );
  }, [execute]);

  return { updateReport, loading, error };
}

export const useDeleteReport = () => {
  const { execute, loading, error } = useMutation('reports');

  const deleteReport = useCallback(async (id, options = {}) => {
    return execute(
      async () => {
        const { error } = await supabase
          .from('reports')
          .delete()
          .eq('id', id);

        return { data: { id }, error };
      },
      {
        operation: 'delete',
        optimisticData: { id },
        ...options
      }
    );
  }, [execute]);

  return { deleteReport, loading, error };
}

/**
 * Season Mutations
 */
export const useCreateSeason = () => {
  const { execute, loading, error } = useMutation('seasons');

  const createSeason = useCallback(async (seasonData, options = {}) => {
    return execute(
      async () => {
        const { data, error } = await supabase
          .from('seasons')
          .insert(seasonData)
          .select()
          .single();

        return { data, error };
      },
      {
        operation: 'create',
        optimisticData: seasonData,
        ...options
      }
    );
  }, [execute]);

  return { createSeason, loading, error };
}

export const useUpdateSeason = () => {
  const { execute, loading, error } = useMutation('seasons');

  const updateSeason = useCallback(async (id, updates, options = {}) => {
    return execute(
      async () => {
        const { data, error } = await supabase
          .from('seasons')
          .update(updates)
          .eq('id', id)
          .select()
          .single();

        return { data, error };
      },
      {
        operation: 'update',
        optimisticData: { id, ...updates },
        ...options
      }
    );
  }, [execute]);

  return { updateSeason, loading, error };
}

/**
 * Invite Ticket Mutations
 */
export const useCreateInviteTicket = () => {
  const { execute, loading, error } = useMutation('invite_tickets');

  const createInviteTicket = useCallback(async (ticket, options = {}) => {
    return execute(
      async () => {
        const { data, error } = await supabase
          .from('invite_tickets')
          .insert(ticket)
          .select()
          .single();

        return { data, error };
      },
      {
        operation: 'insert',
        ...options
      }
    );
  }, [execute]);

  return { createInviteTicket, loading, error };
}

export const useCreateInviteTicketActivation = () => {
  const { execute, loading, error } = useMutation('invite_ticket_activations');

  const createInviteTicketActivation = useCallback(async (activation, options = {}) => {
    return execute(
      async () => {
        const { data, error } = await supabase
          .from('invite_ticket_activations')
          .insert(activation)
          .select()
          .single();

        return { data, error };
      },
      {
        operation: 'insert',
        ...options
      }
    );
  }, [execute]);

  return { createInviteTicketActivation, loading, error };
}

/**
 * Billing Mutations
 */

// Manual billing row creation — fallback for users missed by the auto-provisioning
// trigger (trg_users_provision_billing). The trg_billing_provision_storage trigger
// fires on insert and initialises user_storage limits automatically.
export const useCreateBilling = () => {
  const { execute, loading, error } = useMutation('billing');

  const createBilling = useCallback(async (billingData, options = {}) => {
    return execute(
      async () => {
        const { data, error } = await supabase
          .from('billing')
          .insert(billingData)
          .select()
          .single();

        return { data, error };
      },
      {
        operation: 'create',
        optimisticData: billingData,
        ...options
      }
    );
  }, [execute]);

  return { createBilling, loading, error };
}

// Update billing settings — recurring plan details or manual tier override.
// trg_billing_sync_storage fires automatically on UPDATE and recalculates
// user_storage limits whenever active_tier_code or total_credits change.
// Caller should invoke supabaseGetter.billing.invalidate() in onSuccess to
// flush the billing + user_billing_status + billing_transactions caches.
export const useUpdateBilling = () => {
  const { execute, loading, error } = useMutation('billing');

  const updateBilling = useCallback(async (userId, updates, options = {}) => {
    return execute(
      async () => {
        const { data, error } = await supabase
          .from('billing')
          .update(updates)
          .eq('user_id', userId)
          .select()
          .single();

        return { data, error };
      },
      {
        operation: 'update',
        optimisticData: { user_id: userId, ...updates },
        ...options
      }
    );
  }, [execute]);

  return { updateBilling, loading, error };
}

